import { CommonModule } from '@angular/common';
import {
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { Router } from '@angular/router';
import { ROUTES } from '@constants/routes';
import { resetStores } from '@datorama/akita';
import { Gender } from '@models/Gender';
import { QuoteSession } from '@models/QuoteSession';
import { Species } from '@models/Species';
import { ExperimentationService } from '@services/experimentation.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { QuoteSyncService } from '@services/quote-sync.service';
import { UtmService } from '@services/utm.service';
import { ModalDialogService } from '@shared/modal-dialog/modal-dialog.service';
import { SharedModule } from '@shared/shared.module';
import { getUUID } from '@statsig/js-client';
import { StatsigFlagsDialogComponent } from '@ui-core/statsig-flags-dialog/statsig-flags-dialog.component';
import { ReCaptchaV3Service } from 'ng-recaptcha';

import { firstValueFrom } from 'rxjs';
import {
	first,
} from 'rxjs/operators';

import { QuoteEngineCookieService } from '../../../app/quote-engine-cookie.service';
import { environment } from '../../../environments/environment';

import { ApplicationService } from './../../store/application/application.service';
import { QuoteStore } from './../../store/quote/quote.store';
import { QuoteSessionQuery } from './../../store/session/session.query';
import { QuoteSessionService } from './../../store/session/session.service';

@Component({
	selector: 'qe-environment-banner',
	templateUrl: './environment-banner.component.html',
	styleUrls: ['./environment-banner.component.scss'],
	standalone: true,
	imports: [CommonModule, SharedModule],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EnvironmentBannerComponent {
	public readonly isProdEnvironment: boolean = environment.production;

	public get connectedTabCount$() {
		return this.quoteSync.connectionCount$;
	}

	public get userId(): string | undefined {
		return this.sessionGetters.contactIdValue();
	}

	public get syncModeLabel(): 'H' | 'C' | '-' {
		const role = this.quoteSync.getConnectionStatus().currentRole;

		if (role === 'host') {
			return 'H';
		}

		if (role === 'client') {
			return 'C';
		}

		return '-';
	}

	public get syncStatusTitle(): string {
		const status = this.quoteSync.getConnectionStatus();
		const role = status.currentRole === 'disconnected' ? 'disconnected' : status.currentRole;
		const directConnectionCount = status.connectionCount;
		const totalConnectionCount = this.quoteSync.connectionCount$.getValue();

		return `Tab Sync: ${role}. Direct connections: ${directConnectionCount}, Total connections: ${totalConnectionCount}`;
	}

	public get isSyncConnected(): boolean {
		return this.quoteSync.getConnectionStatus().isConnected;
	}

	public get syncStatusClassName(): string {
		const role = this.quoteSync.getConnectionStatus().currentRole;

		if (role === 'host') {
			return 'environment-warning__sync-status--host';
		}

		if (role === 'client') {
			return 'environment-warning__sync-status--client';
		}

		return 'environment-warning__sync-status--disconnected';
	}

	constructor(
		private readonly router: Router,
		private readonly appActions: ApplicationService,
		private readonly session: QuoteSessionService,
		private readonly sessionGetters: QuoteSessionQuery,
		private readonly cookieService: QuoteEngineCookieService,
		private readonly utmService: UtmService,
		private readonly orchestratorService: OrchestratorService,
		private readonly quoteStore: QuoteStore,
		private readonly recaptchaV3Service: ReCaptchaV3Service,
		private readonly experimentationService: ExperimentationService,
		private readonly modalDialogService: ModalDialogService,
		private readonly quoteSync: QuoteSyncService
	) {

	}

	public get environmentId(): string {
		return environment.appEnvironment.toUpperCase();
	}

	/**
	 * For debugging
	 */
	public resetSession(): void {
		this.cookieService.clear();
		this.clearStorage();
		window.location.reload();
	}

	/**
	 * Copy user ID to clipboard
	 */
	public copyUserIdToClipboard(): void {
		const userId = this.userId;
		if (userId) {
			navigator.clipboard.writeText(userId).then(() => {
				console.log('User ID copied to clipboard:', userId);
				// You could add a toast notification here if needed
			}).catch(err => {
				console.error('Failed to copy user ID to clipboard:', err);
			});
		} else {
			console.warn('No user ID available to copy');
		}
	}

	public async generateQuote(species: string): Promise<void> {
		this.clearStorage();
		console.log('Generating quote with test data...');

		// Get recaptcha token
		const recaptchaToken = await firstValueFrom(this.recaptchaV3Service.execute('start_quote'));

		// Get current session and contact info
		const contact = this.sessionGetters.getContact();
		// const session = await firstValueFrom(this.sessionGetters.session$());
		const analyticsInfo = this.utmService.getUtmCookie();

		const dogData = {
			name: 'Dunkin McButterbeans',
			species: Species.Dog,
			breedId: '452',
			gender: Gender.Male,
			breedName: 'Mixed Breed - Medium (31-50 lbs)',
			isServiceDog: false,
			ageInYears: '1'
		};

		const catData = {
			name: 'Mr Meows',
			species: Species.Cat,
			breedId: '270',
			gender: Gender.Male,
			breedName: 'Domestic Shorthair',
			isServiceDog: false,
			ageInYears: '1'
		};

		const quoteContactData = {
			ratingZipCode: '44128',
			emailAddress: `generate-${getUUID()}@yopmail.com`
		};

		// Show loader
		this.appActions.setLoading(true);

		const generateQuoteRequest: Partial<QuoteSession> = {
			contact: {
				ratingZipCode: quoteContactData.ratingZipCode,
				emailAddress: quoteContactData.emailAddress,
				optInTextMessage: true,
				partnerSenderId: contact?.partnerSenderId ?? '',
				brand: contact?.brand ?? ''
			},
			pets: [species === Species.Dog ? dogData : catData],
			analyticsInfo,
			verificationInfo: {
				expectedAction: 'start_quote',
				captchaToken: recaptchaToken
			}
		};

		// Update quote store
		this.quoteStore.update(generateQuoteRequest);

		console.log('Submitting quote request...', generateQuoteRequest);

		// Submit the quote request
		this.orchestratorService
			.generateNewQuote(generateQuoteRequest)
			.pipe(first())
			.subscribe({
				next: (quoteSession) => {
					if (quoteSession) {
						console.log('Quote generated successfully:', quoteSession);

						// Update session with quote details
						this.session.update({
							...quoteSession,
							quoteDetails: {
								...quoteSession.quoteDetails
							},
							contact: {
								...quoteSession.contact,
							}
						});

						// Set active pet
						this.appActions.setActivePet(quoteSession.pets[0].petId ?? '');

						// Navigate to select policy page
						this.router.navigate([ROUTES.SELECT_A_POLICY]);
					}
				},
				error: (err) => {
					console.error('Error generating quote:', err);
					this.appActions.setLoading(false);
				}
			});
	}

	public openStatsigFlagsDialog(): void {
		const flags = this.experimentationService.getEvaluatedExperiments();
		const stableId = this.experimentationService.getStableId();
		const userId = this.userId;

		if (flags.length > 0) {
			const unassignedCount = flags.filter(f => f.value === 'unassigned').length;
			if (unassignedCount > 0) {
				console.info(`${unassignedCount} experiment(s) are unassigned (not configured in Statsig or user not targeted)`);
			}
			console.log(`Statsig experiments/layers evaluated: ${flags.length}`);
		}

		this.modalDialogService.open(StatsigFlagsDialogComponent, {
			data: { flags, stableId, userId },
			width: '600px'
		});
	}

	public closeStatsigFlagsDialog(): void {
		// Dialog is now handled by ModalDialogService - no need for manual close
	}

	private clearStorage(): void {
		resetStores();
		!!localStorage && localStorage.clear();
		!!sessionStorage && sessionStorage.clear();
	}
}
